import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, exhaustMap, map, of, switchMap } from 'rxjs';
import { TenderSaleActions } from './tender-sale.actions';
import { TenderService } from '@/core/services/tender.service';
import { ApuCategoryService } from '@/core/services/apu-category.service';
import { ApuResourceService } from '@/core/services/apu-resource.service';
import { ApuCategoryApiModel } from '@/core/models/api/apu-category-api.model';
import { ApuResourceApiModel } from '@/core/models/api/apu-resource-api.model';
import { TenderApiModel } from '@/core/models/api/tender-api-model.model';
import { removeRelationshipsFromApuCategory } from '@/utils/apu-category.utils';

@Injectable()
export class TenderSaleEffects {
  private actions$ = inject(Actions);
  private tenderService = inject(TenderService);
  private apuCategoryService = inject(ApuCategoryService);
  private apuResourceService = inject(ApuResourceService);

  /**
   * Load tender by projectId using getLatestExecutionTenderByProjectId.
   * On success, the reducer extracts the APU tree automatically.
   */
  loadTender$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TenderSaleActions.loadTender),
      switchMap(({ projectId }) =>
        this.tenderService.getLatestExecutionTenderByProjectId(projectId).pipe(
          map((tender: TenderApiModel) =>{
            console.log('Tender loaded in effect: ', tender);
            return TenderSaleActions.loadTenderSuccess({ tender });
          }
          ),
          catchError((error) =>
            of(TenderSaleActions.loadTenderFail({ error: error?.message ?? 'Error al cargar la licitación' }))
          )
        )
      )
    )
  );

  /**
   * Persist category quantity sale change to the API.
   */
  updateCategoryQuantitySale$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TenderSaleActions.updateCategoryQuantitySale),
      exhaustMap(({ apuCategoryId, quantitySale }) =>
        this.apuCategoryService.getById(apuCategoryId).pipe(
          switchMap((category: ApuCategoryApiModel) => {
            const saleUnitPrice = category.saleUnitPrice ?? 0;
            const updated: ApuCategoryApiModel = {
              ...removeRelationshipsFromApuCategory(category),
              quantitySale,
              saleTotalPrice: saleUnitPrice * quantitySale,
            } as ApuCategoryApiModel;
            return this.apuCategoryService.update(updated).pipe(
              map((saved: ApuCategoryApiModel) =>
                TenderSaleActions.updateCategoryQuantitySaleSuccess({ category: saved })
              )
            );
          }),
          catchError((error) =>
            of(TenderSaleActions.updateCategoryQuantitySaleFail({ error: error?.message ?? 'Error saving quantity' }))
          )
        )
      )
    )
  );

  /**
   * Load resources for a selected category.
   */
  loadResources$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TenderSaleActions.loadResources),
      switchMap(({ apuCategoryId }) =>
        this.apuResourceService.getByApuCategoryId(apuCategoryId).pipe(
          map((resources: any) =>
            TenderSaleActions.loadResourcesSuccess({ resources: resources as ApuResourceApiModel[] })
          ),
          catchError((error) =>
            of(TenderSaleActions.loadResourcesFail({ error: error?.message ?? 'Error loading resources' }))
          )
        )
      )
    )
  );

  /**
   * Batch update resources (dirty save).
   */
  updateResourcesBatch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TenderSaleActions.updateResourcesBatch),
      exhaustMap(({ resources }) =>
        this.apuResourceService.updateBatch(resources).pipe(
          map((saved: any) =>
            TenderSaleActions.updateResourcesBatchSuccess({ resources: saved as ApuResourceApiModel[] })
          ),
          catchError((error) =>
            of(TenderSaleActions.updateResourcesBatchFail({ error: error?.message ?? 'Error saving resources' }))
          )
        )
      )
    )
  );
}
